import { Directive, ElementRef, Input, OnChanges, OnDestroy, OnInit, Renderer2, SimpleChanges } from '@angular/core';
import { NgControl } from '@angular/forms';
import { FieldErrorService } from '@app-core';
import { Subscription } from 'rxjs';

@Directive({
    selector: '[appShowFieldErrors]',
    standalone: true,
})
export class FieldErrorDirective implements OnInit, OnDestroy, OnChanges {
    @Input('appShowFieldErrors') fieldName: string = 'Field'; // Control name for dynamic messages
    @Input() formControlName: string;
    private errorElement: HTMLElement;
    private statusChangesSubscription: Subscription;
    private blurEventListener: () => void;

    constructor(
        private readonly element: ElementRef,
        private readonly renderer: Renderer2,
        private readonly control: NgControl,
        private readonly fieldErrorService: FieldErrorService,
    ) {}

    ngOnInit(): void {
        if (!this.fieldName) {
            this.fieldName = 'Field';
        }

        this.errorElement = document.createElement('div');
        this.errorElement.className = 'invalid-feedback';

        // Check if the control is part of a radio button group
        const parentElement = this.element.nativeElement.closest('.form-group');
        if (parentElement) {
            parentElement.appendChild(this.errorElement);
        } else {
            this.element.nativeElement.insertAdjacentElement('afterend', this.errorElement);
        }

        // Fires the directive on status changes of the control
        this.statusChangesSubscription = this.control.statusChanges.subscribe(() => {
            this.updateErrorMessage();
        });
        // Fires the directive on blur event of input
        this.blurEventListener = this.renderer.listen(this.element.nativeElement, 'blur', () => {
            this.updateErrorMessage();
        });

        this.updateErrorMessage();
    }

    ngOnChanges(changes: SimpleChanges): void {
        // When fieldName changes, update error message
        if (changes['fieldName'] && !changes['fieldName'].firstChange) {
            this.updateErrorMessage();
        }
    }

    ngOnDestroy(): void {
        if (this.statusChangesSubscription) {
            this.statusChangesSubscription.unsubscribe();
        }
        if (this.blurEventListener) {
            this.blurEventListener();
        }

        this.removeErrorElement();
    }

    private updateErrorMessage() {
        const errors = this.control.errors;
        const control = this.control.control;
        if (errors && (control.touched || control.dirty)) {
            if (!this.errorElement) {
                this.createErrorElement();
            }
            const errorMessage = this.fieldErrorService.getErrorMessage(this.fieldName, errors);
            this.errorElement.innerHTML = `<i class="bi bi-exclamation-circle"></i> ${errorMessage}`;
        } else {
            this.removeErrorElement();
        }
    }

    private createErrorElement() {
        this.errorElement = document.createElement('div');
        this.errorElement.className = 'invalid-feedback';

        const parentElement = this.element.nativeElement.closest('.form-group');
        if (parentElement) {
            parentElement.insertAdjacentElement('afterend', this.errorElement);
        } else {
            this.element.nativeElement.insertAdjacentElement('afterend', this.errorElement);
        }
    }

    private removeErrorElement() {
        if (this.errorElement) {
            this.errorElement.remove();
            this.errorElement = null;
        }
    }
}
